import React, { useState, useEffect } from 'react';
import { Edit2, Trash2, Image as ImageIcon, X, Loader } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import imageStorage from '../utils/imageStorage';

const TransactionMenu = ({ transaction, onUpdate, onDelete }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [localImageUrl, setLocalImageUrl] = useState(null);
  const [loadingImage, setLoadingImage] = useState(false);
  const [editData, setEditData] = useState({
    merchant: transaction.merchant || '',
    description: transaction.description || '',
    amount: transaction.amount,
    date: transaction.date,
    category: transaction.category,
  });

  // Try to load image from IndexedDB when modal opens
  useEffect(() => {
    if (showImageModal && transaction.id) {
      loadImage();
    }
  }, [showImageModal, transaction.id]);

  const loadImage = async () => {
    setLoadingImage(true);
    try {
      // First try IndexedDB
      const localImage = await imageStorage.getImage(transaction.id);
      if (localImage?.imageData) {
        setLocalImageUrl(`data:image/png;base64,${localImage.imageData}`);
        setLoadingImage(false);
        return;
      }
    } catch (err) {
      console.log('IndexedDB not available:', err.message);
    }
    
    // Fetch from API with auth token
    try {
      const response = await api.get(`/receipts/${transaction.id}/image`, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(response.data);
      setLocalImageUrl(blobUrl);
    } catch (err) {
      console.log('API fetch failed:', err.message);
      setLocalImageUrl(null);
    }
    setLoadingImage(false);
  };

  const categories = [
    'Groceries', 'Transport', 'Eating Out', 'Entertainment',
    'Healthcare', 'Shopping', 'Utilities', 'Uncategorized',
    'Salary', 'Freelance', 'Gift', 'Refund', 'Other'
  ];

  const handleEdit = async () => {
    try {
      const endpoint = transaction.type === 'expense'
        ? `/receipts/${transaction.id}`
        : `/income/${transaction.id}`;

      const updateData = transaction.type === 'expense'
        ? {
            merchant: editData.merchant,
            amount: parseFloat(editData.amount),
            date: editData.date,
            category: editData.category,
          }
        : {
            description: editData.description,
            amount: parseFloat(editData.amount),
            date: editData.date,
            category: editData.category,
          };

      await api.put(endpoint, updateData);
      toast.success('Transaction updated!');
      setShowEditModal(false);
      onUpdate();
    } catch (error) {
      console.error('Failed to update:', error);
      toast.error('Failed to update transaction');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this transaction?')) return;

    try {
      const endpoint = transaction.type === 'expense'
        ? `/receipts/${transaction.id}`
        : `/income/${transaction.id}`;

      await api.delete(endpoint);
      toast.success('Transaction deleted!');
      onDelete();
    } catch (error) {
      console.error('Failed to delete:', error);
      toast.error('Failed to delete transaction');
    }
  };

  return (
    <>
      {/* Menu Button */}
      <div className="relative">
        <button
          onClick={() => setShowMenu(!showMenu)}
          data-testid={`menu-${transaction.id}`}
          className="p-2 hover:bg-white/10 rounded-lg transition-colors"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
          </svg>
        </button>

        {showMenu && (
          <div className="absolute right-0 mt-2 w-48 bg-[#0f172a] border border-white/10 rounded-lg shadow-xl z-50">
            <button
              onClick={() => {
                setShowEditModal(true);
                setShowMenu(false);
              }}
              data-testid={`edit-${transaction.id}`}
              className="w-full px-4 py-2 text-left hover:bg-white/10 transition-colors flex items-center space-x-2 rounded-t-lg"
            >
              <Edit2 className="w-4 h-4" />
              <span>Edit</span>
            </button>
            
            {transaction.type === 'expense' && transaction.image_url && (
              <button
                onClick={() => {
                  setShowImageModal(true);
                  setShowMenu(false);
                }}
                data-testid={`view-receipt-${transaction.id}`}
                className="w-full px-4 py-2 text-left hover:bg-white/10 transition-colors flex items-center space-x-2"
              >
                <ImageIcon className="w-4 h-4" />
                <span>View Receipt</span>
              </button>
            )}
            
            <button
              onClick={() => {
                handleDelete();
                setShowMenu(false);
              }}
              data-testid={`delete-${transaction.id}`}
              className="w-full px-4 py-2 text-left hover:bg-red-500/20 text-red-400 transition-colors flex items-center space-x-2 rounded-b-lg"
            >
              <Trash2 className="w-4 h-4" />
              <span>Delete</span>
            </button>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#0a0a0f] border border-white/10 rounded-2xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold">Edit Transaction</h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {transaction.type === 'expense' ? (
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-2">
                    Merchant
                  </label>
                  <input
                    type="text"
                    value={editData.merchant}
                    onChange={(e) => setEditData({ ...editData, merchant: e.target.value })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-2">
                    Description
                  </label>
                  <input
                    type="text"
                    value={editData.description}
                    onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  Amount
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={editData.amount}
                  onChange={(e) => setEditData({ ...editData, amount: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  Date
                </label>
                <input
                  type="text"
                  value={editData.date}
                  onChange={(e) => setEditData({ ...editData, date: e.target.value })}
                  placeholder="MM/DD/YYYY"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  Category
                </label>
                <select
                  value={editData.category}
                  onChange={(e) => setEditData({ ...editData, category: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  {categories.map((cat) => (
                    <option key={cat} value={cat} className="bg-[#0f172a]">
                      {cat}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex space-x-4 pt-4">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 bg-white/10 text-white hover:bg-white/20 px-6 py-3 rounded-full font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEdit}
                  className="flex-1 bg-white text-black hover:bg-white/90 px-6 py-3 rounded-full font-medium transition-all"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Modal */}
      {showImageModal && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="relative max-w-4xl w-full">
            <button
              onClick={() => setShowImageModal(false)}
              className="absolute top-4 right-4 p-2 bg-black/50 hover:bg-black/70 rounded-full transition-colors z-10"
            >
              <X className="w-6 h-6 text-white" />
            </button>
            {loadingImage ? (
              <div className="flex items-center justify-center h-64">
                <Loader className="w-8 h-8 animate-spin text-indigo-400" />
              </div>
            ) : (
              <img
                src={localImageUrl || `${process.env.REACT_APP_BACKEND_URL}/api/receipts/${transaction.id}/image`}
                alt="Receipt"
                className="w-full h-auto max-h-[90vh] object-contain rounded-xl"
                onError={(e) => {
                  // If local image failed and we're using server URL, show error
                  if (!localImageUrl) {
                    e.target.style.display = 'none';
                    toast.error('Receipt image not found');
                    setShowImageModal(false);
                  } else {
                    // Try server URL as fallback
                    setLocalImageUrl(null);
                  }
                }}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default TransactionMenu;
